import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useTabs from "../../../../../hooks/useTabs";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  InsertSubstructureBody,
  Substructure,
  UpdateSubstructureNewImageBody,
  UpdateSubstructureRemoveImageBody,
} from "../../../../../http/border/types/substructures";
import { useSubstructuresApi } from "../../../../../http/border/useSubstructures";
import { getUILanguages } from "../../../../../types";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Tabs from "../../../../ui/Tabs";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface SubstructureFormBody {
  name: string;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface SubstructureDetailsProps {
  substructure?: Substructure;
  isDisabled?: boolean;
  isLoading?: boolean;
  onSubmitSuccess?: (substructureId?: string) => void;
  tabbed?: boolean;
}

const SubstructureForm: FC<SubstructureDetailsProps> = ({
  substructure,
  isDisabled,
  isLoading,
  onSubmitSuccess,
  tabbed,
}: SubstructureDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const substructuresApi = useSubstructuresApi();
  const languages = getUILanguages(i18n.translation);
  const controller = useTabs(languages.length);
  const [tabs, setTabs] = useState([
    {
      title: i18n.translation.common.languages.english,
      value: i18n.translation.common.languages.english,
    },
  ]);

  const {
    component,
    newImage,
    removeImage,
    onSave: onImageSave,
  } = useImageUploader({
    defaultImage: substructure?.image ?? undefined,
    isLoading: !!isLoading,
    disabled: isDisabled || isSubmitting,
  });

  const defaultValues: SubstructureFormBody = {
    name: substructure?.name ?? "",
    nameIt: substructure?.nameIt ?? null,
    nameDe: substructure?.nameDe ?? null,
    nameEs: substructure?.nameEs ?? null,
    nameFr: substructure?.nameFr ?? null,
    order: substructure?.order ?? 50,
    visibility: substructure?.visibility ?? "hidden",
  };

  useEffect(() => {
    setTabs([{ title: i18n.translation.common.languages.english, value: i18n.translation.common.languages.english }]);
  }, [i18n.translation]);

  const onSubmit = () => async (formData: SubstructureFormBody) => {
    setIsSubmitting(true);

    const commonBody = {
      name: formData.name.trim(),
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
    };

    if (substructure) {
      if (newImage) {
        try {
          const signedUrl = await substructuresApi.generateUploadImageUrl(
            substructure.id,
            newImage.name,
            newImage.type,
          );
          const res = await api.uploadFile(signedUrl, newImage);
          if (!res.ok) {
            dispatch(showErrorNotification(i18n, res));
            setIsSubmitting(false);
            return;
          }
          const body: UpdateSubstructureNewImageBody = {
            ...commonBody,
            image: newImage.name,
          };
          await substructuresApi.updateAdminSubstructure(substructure.id, body);
          onImageSave();
          onSubmitSuccess?.();
          dispatch(showSuccessNotification(i18n.translation));
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      } else {
        try {
          const body: UpdateSubstructureRemoveImageBody = {
            ...commonBody,
            removeImage: removeImage,
          };
          await substructuresApi.updateAdminSubstructure(substructure.id, body);
          onImageSave();
          onSubmitSuccess?.();
          dispatch(showSuccessNotification(i18n.translation));
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      }
    } else {
      try {
        const body: InsertSubstructureBody = {
          ...commonBody,
          image: newImage?.name,
        };

        const res = await substructuresApi.insertAdminSubstructure(body);
        if (newImage) {
          const signedUrl = await substructuresApi.generateUploadImageUrl(res.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            setIsSubmitting(false);
            return;
          }
          onImageSave();
        }
        onSubmitSuccess?.(res.id);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const panel = (
    <Form<SubstructureFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || isLoading || isDisabled}
      onSubmit={onSubmit}
    >
      {({ register, reset, formState: { errors }, control, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [substructure]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField
              label={i18n.translation.common.name}
              error={errors.name}
              hidden={!!tabbed && controller.selectedIndex != 0}
            >
              {({ labelId, isInvalid, isOptional }) => (
                <TranslatableField
                  field="name"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.name}
                >
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading || isSubmitting}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting || isLoading || isDisabled}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isSubmitting || isLoading || isDisabled}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );

  return tabbed ? <Tabs controller={controller} tabs={tabs} contents={[panel]} /> : panel;
};

export default SubstructureForm;
